import { Field } from '@/components/ui/field';
import { InputGroup } from '@/components/ui/input-group';
import { useUpdateBrandDetailsAction } from '@/hooks/actions';
import { Box, Fieldset, Input, SimpleGrid } from '@chakra-ui/react';
import { isEuMemberCountry } from '@fluenceur/types';
import { CountrySelect } from '@fluenceur/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import BrandVatNumberField from './BrandVatNumberField';

// Create schema function that uses translations
const createFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(1, t('settings.brandInformationForm.validation.nameRequired')),
    websiteUrl: z.string().url(t('settings.brandInformationForm.validation.websiteUrlInvalid')),
    city: z.string().min(1, t('settings.brandInformationForm.validation.cityRequired')),
    addressLine1: z.string().min(1, t('settings.brandInformationForm.validation.addressRequired')),
    addressLine2: z.string().nullable(),
    postalCode: z.string().min(1, t('settings.brandInformationForm.validation.postalCodeRequired')),
    country: z.string().min(1, t('settings.brandInformationForm.validation.countryRequired')),
    registrationNumber: z.string(),
    // Shape is never enforced here. A number that does not match its country
    // still saves, and the field only ever raises advisory copy beside it.
    vatNumber: z.string(),
  });

export type BrandInformationFormData = z.infer<ReturnType<typeof createFormSchema>>;

export interface BrandInformationFormProps {
  defaultValues: Partial<BrandInformationFormData>;
  onSuccess: () => void;
  onSubmitting: (isSubmitting: boolean) => void;
}

export interface BrandInformationFormRef {
  submit: () => void;
}

const BrandInformationForm = forwardRef<BrandInformationFormRef, BrandInformationFormProps>(
  ({ defaultValues, onSuccess, onSubmitting }, ref) => {
    const { t } = useTranslation();
    const formRef = useRef<HTMLFormElement>(null);
    const navigate = useNavigate();
    const updateBrandDetailsMutation = useUpdateBrandDetailsAction();

    const formSchema = createFormSchema(t);

    const {
      register,
      handleSubmit,
      control,
      watch,
      formState: { errors },
    } = useForm<BrandInformationFormData>({
      resolver: zodResolver(formSchema),
      defaultValues: defaultValues as BrandInformationFormData,
    });

    // Read from the form, not from the saved brand, so correcting the country
    // offers the VAT field straight away rather than after a save and a reopen.
    const selectedCountry = watch('country') ?? '';
    const vatNumber = watch('vatNumber') ?? '';

    useEffect(() => {
      onSubmitting(updateBrandDetailsMutation.isPending);
    }, [onSubmitting, updateBrandDetailsMutation.isPending]);

    const onSubmit = (data: BrandInformationFormData) => {
      updateBrandDetailsMutation.mutate(data, {
        onSuccess: () => {
          onSuccess(); // Close the modal
          // Refresh the page to show updated data
          navigate('.', { replace: true });
        },
      });
    };

    useImperativeHandle(ref, () => ({
      submit: () => {
        formRef.current?.requestSubmit();
      },
    }));

    return (
      <Box as="form" ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <Fieldset.Root colorPalette="primary">
          <Fieldset.Content>
            <Field
              label={t('settings.brandInformationForm.brandName')}
              errorText={errors.name?.message}
              invalid={!!errors.name}
              required
            >
              <Input {...register('name')} variant="outline" />
            </Field>

            <Field
              label={t('settings.website')}
              errorText={errors.websiteUrl?.message}
              invalid={!!errors.websiteUrl}
              required
            >
              <InputGroup w="full">
                <Input {...register('websiteUrl')} variant="outline" placeholder="example.com" />
              </InputGroup>
            </Field>

            <Field
              label={t('settings.brandInformationForm.addressLine1')}
              errorText={errors.addressLine1?.message}
              invalid={!!errors.addressLine1}
              required
            >
              <Input {...register('addressLine1')} variant="outline" />
            </Field>

            <Field
              label={t('settings.brandInformationForm.addressLine2')}
              errorText={errors.addressLine2?.message}
              invalid={!!errors.addressLine2}
            >
              <Input {...register('addressLine2')} variant="outline" />
            </Field>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              <Field
                label={t('settings.city')}
                errorText={errors.city?.message}
                invalid={!!errors.city}
                required
              >
                <Input {...register('city')} variant="outline" />
              </Field>

              <Field
                label={t('settings.postalCode')}
                errorText={errors.postalCode?.message}
                invalid={!!errors.postalCode}
                required
              >
                <Input {...register('postalCode')} variant="outline" />
              </Field>
            </SimpleGrid>

            <Field
              label={t('settings.country')}
              errorText={errors.country?.message}
              invalid={!!errors.country}
              required
            >
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <CountrySelect
                    placeholder={t('settings.brandInformationForm.selectCountry')}
                    {...(field.value && { value: field.value })}
                    onValueChange={field.onChange}
                    name={field.name}
                    zIndex={99999}
                  />
                )}
              />
            </Field>

            {/* No format check, deliberately: the server validates presence
                alone. Values already stored are unreliable, no migration can
                guess what a brand meant to write, and a pattern strict enough
                for a SIRET refuses legitimate foreign registrations. */}
            <Field
              label={t('settings.registrationNumber')}
              helperText={t('settings.registrationNumberHelp')}
              errorText={errors.registrationNumber?.message}
              invalid={!!errors.registrationNumber}
            >
              <Input {...register('registrationNumber')} variant="outline" />
            </Field>

            {isEuMemberCountry(selectedCountry) && (
              <BrandVatNumberField
                country={selectedCountry}
                value={vatNumber}
                registration={register('vatNumber')}
              />
            )}
          </Fieldset.Content>
        </Fieldset.Root>
      </Box>
    );
  }
);

BrandInformationForm.displayName = 'BrandInformationForm';

export default BrandInformationForm;
